import React, { useMemo, useState } from 'react';
import Icon from '@ant-design/icons';
import classNames from 'classnames';
import Badge from 'antd/lib/badge';
import styles from './BaseServiceIcon.module.css';
import InjectSvg from '@components/InjectSvg/InjectSvg';
import { calculateWidgetColors } from '@components/gates/common';
import GateIcon from '@components/gateIcon/gateIcon';
import { ReactComponent as DefaultServiceIcon } from '@src/assets/img/gates/hubService.svg';

type Props = {
  /** Optional svg icon contents. */
  svgIcon?: string;
  /** Optional icon base color. */
  color?: string;
  /** Optional default color when no explicit color is provided. */
  defaultColor?: string;
  /** Optional className for wrapper styling. */
  className?: string;
  /** Whether the icon is selected. */
  selected?: boolean;
  /** Whether the service is in dev mode. */
  devMode?: boolean;
};

const DefaultServiceColorVar = '--kemu-color-widget-type-custom';

/**
 * Renders a hub service icon with consistent theming and optional injected svg support.
 * @returns the rendered icon element
 */
const BaseServiceIcon = (props: Props): React.JSX.Element => {
  const { svgIcon, color, defaultColor, className, selected, devMode } = props;
  const [iconTarget, setIconTarget] = useState<HTMLElement | null>(null);

  const colors = useMemo(() => {
		const docStyle = getComputedStyle(document.body);
    const widgetColor = color || defaultColor;
    const customWidgetColor = docStyle.getPropertyValue(DefaultServiceColorVar);
		return calculateWidgetColors(widgetColor || customWidgetColor);
	}, [color, defaultColor]);

  const iconElement = (
    <div
      className={classNames(styles.Icon, className, {
        [styles.Selected]: selected,
      })}
      ref={(e) => setIconTarget(e)}
      style={{ backgroundColor: colors.content }}
    >
      {svgIcon ? (
        <InjectSvg
          contents={svgIcon}
          fill={colors.text}
          targetElement={iconTarget}
        />
      ) : (
        <GateIcon
          style={{ backgroundColor: colors.content }}
          icon={<Icon component={DefaultServiceIcon} />}
        />
      )}
    </div>
  );

  if (devMode) {
    return (
      <Badge
        count="dev"
        size="small"
        offset={[-5, 0]}
        color="red"
      >
        {iconElement}
      </Badge>
    );
  }

  return iconElement;
};

export default BaseServiceIcon;


